// src/pages/customer/CustomerAccountPage.js
// 后台 · 客户中心 · 客户户号管理（doc01 3.7，缺陷 120/121）
// 结构：筛选（客户简称 / 用电户号 / 用电类别）置于表格上方 + 查询·重置；表上方「新增客户户号」。
//  列：客户简称｜用电户号｜用电类别｜关联站点个数｜新增时间｜操作（编辑｜查看｜删除）。
//  新增/编辑弹窗表单（* 为必填，用电户号全局唯一）；删除二次确认；本地增删改真实生效。
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Button, Input, Select, Modal, Space, Popconfirm, Tag, message } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import PageSkeleton from '../../components/PageSkeleton';
import { findMenu, pick } from '../../config/menuConfig';
import { H_CUSTOMERS, H_CUSTOMER_CATEGORIES, STATIONS } from '../../config/stationData';

const labelStyle = { color: 'rgba(255,255,255,0.55)', fontSize: 12 };
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { short: '', full: '', no: '', category: undefined, region: '', location: '', contact: '', phone: '', nodes: [] };
const CAT_OPTIONS = H_CUSTOMER_CATEGORIES.map((v) => ({ value: v, label: v }));
const STATION_OPTIONS = STATIONS.map((s) => ({ value: s.name, label: s.name }));

const CustomerAccountPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const meta = findMenu('/app/customer/account');

  const [rows, setRows] = useState(() => H_CUSTOMERS.map((c) => ({ ...c })));
  const [fShort, setFShort] = useState('');
  const [fNo, setFNo] = useState('');
  const [fCat, setFCat] = useState(undefined);
  const [applied, setApplied] = useState({ short: '', no: '', cat: undefined });

  const [modal, setModal] = useState({ open: false, editing: null, form: EMPTY });
  const [detail, setDetail] = useState(null);

  const filtered = useMemo(() => rows.filter((r) => (
    (!applied.short || String(r.short || '').includes(applied.short.trim()))
    && (!applied.no || String(r.no || '').includes(applied.no.trim()))
    && (!applied.cat || r.category === applied.cat)
  )), [rows, applied]);

  const doQuery = () => { setApplied({ short: fShort, no: fNo, cat: fCat }); message.success('已按条件查询'); };
  const doReset = () => { setFShort(''); setFNo(''); setFCat(undefined); setApplied({ short: '', no: '', cat: undefined }); message.success('查询条件已重置'); };

  const openAdd = () => setModal({ open: true, editing: null, form: { ...EMPTY } });
  const openEdit = (r) => setModal({
    open: true,
    editing: r.key,
    form: {
      short: r.short || '', full: r.full || '', no: r.no || '', category: r.category,
      region: r.region || '', location: r.location || '', contact: r.contact || '',
      phone: r.phone || '', nodes: r.nodes || [],
    },
  });
  const setField = (k, v) => setModal((s) => ({ ...s, form: { ...s.form, [k]: v } }));

  const submit = () => {
    const f = modal.form;
    if (!f.short.trim()) { message.warning('请输入客户简称'); return; }
    if (!f.no.trim()) { message.warning('请输入用电户号'); return; }
    if (!f.category) { message.warning('请选择用电类别'); return; }
    if (rows.some((r) => r.no === f.no.trim() && r.key !== modal.editing)) { message.warning('用电户号为全局唯一，请更换'); return; }
    const payload = {
      short: f.short.trim(), full: f.full.trim(), no: f.no.trim(), category: f.category,
      region: f.region.trim(), location: f.location.trim(), contact: f.contact.trim(),
      phone: f.phone.trim(), nodes: f.nodes, stations: f.nodes.length,
    };
    if (modal.editing != null) {
      setRows((list) => list.map((r) => (r.key === modal.editing ? { ...r, ...payload } : r)));
      message.success(`客户户号「${payload.no}」已更新`);
    } else {
      setRows((list) => [{ key: `c-${Date.now()}`, createdAt: today(), ...payload }, ...list]);
      message.success(`客户户号「${payload.no}」已新增`);
    }
    setModal({ open: false, editing: null, form: EMPTY });
  };

  const remove = (r) => {
    setRows((list) => list.filter((x) => x.key !== r.key));
    message.success(`客户户号「${r.no}」已删除`);
  };

  const columns = [
    { title: '客户简称', dataIndex: 'short', render: (v) => <span style={{ color: '#00d4ff' }}>{v}</span> },
    { title: '用电户号', dataIndex: 'no' },
    { title: '用电类别', dataIndex: 'category', render: (v) => (v ? <Tag color="blue" style={{ margin: 0 }}>{v}</Tag> : '-') },
    { title: '关联站点个数', dataIndex: 'stations', width: 130, render: (v) => `${v || 0} 个` },
    { title: '新增时间', dataIndex: 'createdAt', width: 130 },
    {
      title: '操作', key: 'op', fixed: 'right', width: 170,
      render: (_, r) => (
        <Space size={2} wrap>
          <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button>
          <Button type="link" size="small" onClick={() => setDetail(r)}>查看</Button>
          <Popconfirm title={`确认删除户号「${r.no}」？`} onConfirm={() => remove(r)} okText="确定" cancelText="取消">
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const d = detail || {};
  const f = modal.form;

  return (
    <PageSkeleton
      title={meta ? pick(meta.label, lang) : '客户户号管理'}
      desc={meta ? pick(meta.desc, lang) : ''}
      crumb={meta ? [pick(meta.groupLabel, lang), pick(meta.sectionLabel, lang), pick(meta.label, lang)] : []}
      filters={[]}
      actions={[]}
    >
      <div className="panel">
        <div className="panel-title">客户户号<small>共 {filtered.length} 个户号，默认 10 行分页，按新增时间降序</small></div>

        {/* 筛选置于表格上方 */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 10 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={labelStyle}>客户简称</span>
            <Input size="small" value={fShort} onChange={(e) => setFShort(e.target.value)} placeholder="请输入客户简称模糊查询" prefix={<SearchOutlined />} style={{ width: 200 }} allowClear />
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={labelStyle}>用电户号</span>
            <Input size="small" value={fNo} onChange={(e) => setFNo(e.target.value)} placeholder="请输入用电户号模糊查询" prefix={<SearchOutlined />} style={{ width: 200 }} allowClear />
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={labelStyle}>用电类别</span>
            <Select size="small" value={fCat} onChange={setFCat} placeholder="全部" allowClear style={{ width: 160 }} options={CAT_OPTIONS} />
          </span>
          <Space>
            <Button type="primary" size="small" onClick={doQuery}>查询</Button>
            <Button size="small" onClick={doReset}>重置</Button>
          </Space>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAdd}>新增客户户号</Button>
        </div>

        <Table rowKey="key" dataSource={filtered} columns={columns} pagination={{ pageSize: 10, size: 'small' }} size="middle" scroll={{ x: 900 }} />
      </div>

      {/* 新增 / 编辑 */}
      <Modal
        title={modal.editing != null ? '编辑客户户号' : '新增客户户号'}
        open={modal.open}
        onOk={submit}
        onCancel={() => setModal({ open: false, editing: null, form: EMPTY })}
        okText="确定" cancelText="取消" destroyOnHidden width={640}
      >
        <div style={{ marginBottom: 10, color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>* 为必填项，用电户号全局唯一；一个用电户号可关联多个电站。</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div><div style={labelStyle}>客户简称 *</div><Input value={f.short} onChange={(e) => setField('short', e.target.value)} placeholder="请输入客户简称" /></div>
          <div><div style={labelStyle}>客户全称</div><Input value={f.full} onChange={(e) => setField('full', e.target.value)} placeholder="请输入客户全称" /></div>
          <div><div style={labelStyle}>用电户号 *</div><Input value={f.no} onChange={(e) => setField('no', e.target.value)} placeholder="请输入用电户号（全局唯一）" /></div>
          <div><div style={labelStyle}>用电类别 *</div><Select style={{ width: '100%' }} value={f.category} onChange={(v) => setField('category', v)} placeholder="请选择用电类别" options={CAT_OPTIONS} /></div>
          <div><div style={labelStyle}>所在区域</div><Input value={f.region} onChange={(e) => setField('region', e.target.value)} placeholder="请输入所在区域" /></div>
          <div><div style={labelStyle}>客户所在地</div><Input value={f.location} onChange={(e) => setField('location', e.target.value)} placeholder="请输入客户所在地" /></div>
          <div><div style={labelStyle}>联系人</div><Input value={f.contact} onChange={(e) => setField('contact', e.target.value)} placeholder="请输入联系人" /></div>
          <div><div style={labelStyle}>联系电话</div><Input value={f.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="请输入联系电话" /></div>
          <div style={{ gridColumn: '1 / span 2' }}>
            <div style={labelStyle}>关联电站</div>
            <Select mode="multiple" style={{ width: '100%' }} value={f.nodes} onChange={(v) => setField('nodes', v)} placeholder="多选下拉选择电站" options={STATION_OPTIONS} />
          </div>
        </div>
      </Modal>

      {/* 查看 */}
      <Modal
        title={`客户户号详情 · ${d.no || ''}`}
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={<Button type="primary" onClick={() => setDetail(null)}>关闭</Button>}
        destroyOnHidden
      >
        <div className="dev-kv">
          {[
            ['客户简称', d.short], ['客户全称', d.full], ['用电户号', d.no], ['用电类别', d.category],
            ['所在区域', d.region], ['客户所在地', d.location], ['联系人', d.contact], ['联系电话', d.phone],
            ['关联站点个数', d.nodes ? `${d.nodes.length} 个` : `${d.stations || 0} 个`],
            ['关联站点', d.nodes && d.nodes.length ? d.nodes.join('、') : '-'],
            ['新增时间', d.createdAt],
          ].map(([k, v]) => (
            <div className="dev-kv-row" key={k}>
              <span className="dev-kv-k">{k}</span>
              <span className="dev-kv-v">{v == null || v === '' ? '-' : v}</span>
            </div>
          ))}
        </div>
      </Modal>
    </PageSkeleton>
  );
};

export default CustomerAccountPage;
